// @ts-strict-ignore
import React from 'react'
import { IonLoading, IonToast } from '@ionic/react'
import mapboxgl from 'mapbox-gl'

import { CollectionItem, ScreenSection, AppMetaData, MediaItemsStoreState } from 'mytours-read-common'
import { GetCollectionItemsIndexResponseData, MapItem } from 'mytours-read-api-typescript'

import { generateMapboxStyleUrl, getStyleProperty, isRTL, getCurrentLanguage } from '../../platform'
import { DEFAULT_APP_INFO, loadAppMetaDataFromDisk } from '../../settings/functions'
import {
	getBounds,
	getDirectionRoute,
	getCurrentPosition,
	findClosestPointIndex,
	formatMapPinIcon,
	buildPointMapPinPopup,
} from '../functions'

import { MapControls, SimpleCoordinates, RouteProfile, MapContainer } from '../types'

import strings from '../../../strings'

import 'mapbox-gl/dist/mapbox-gl.css'
import { generateMapPinIconUrl } from 'common_lib/maps/functions'
import { ionHistory } from 'mobile_web/modules/navigation'
import { ElevationGraph } from './ElevationGraph'

interface Props extends MapContainer {
	items: CollectionItem[]
	currentItemIndex: number
	mapControls?: (params: MapControls) => JSX.Element
	extractMapboxMapStyle?: () => string | undefined
	onCloseMap?: () => void
	mapStyle?: string
	boundAll?: boolean
	onSetActiveItem?: (item: CollectionItem | ScreenSection | MapItem, itemIndex: number) => void
	defaultZoom?: number
	mapRoutes?: any[]
	showDirections?: boolean
	routeProfile?: RouteProfile
	endCoordinates?: SimpleCoordinates
	setLegsData?: (legsData: any) => void
	showInAppDirections?: (showInAppDirections: boolean) => void
	onMapReady?: () => void
	isItemsLoaded?: boolean
	onMapResize?(): void
	mediaItems?: MediaItemsStoreState
	loadMediaItem?: (id: number, version: number, language: string) => void
	elevationEnabled?: boolean
}

interface State {
	collectionItemPins: { lat: number, lng: number }[]
	markerListeners: EventListenerOrEventListenerObject[]
	mapContainerReady: boolean
	coordinates: SimpleCoordinates
	loading: boolean
	error: string
	map: mapboxgl.Map
	mediaItemsLoaded: boolean
	// Elevation
	currentRoute?: [number, number, number][]
	selectedRoutePointIndex?: number
	elevationOpened: boolean
}

const INITIAL_STATE: State = {
	collectionItemPins: [],
	markerListeners: [],
	mapContainerReady: false,
	coordinates: undefined,
	loading: true,
	error: '',
	map: null,
	mediaItemsLoaded: false,
	elevationOpened: false,
}

export default class MapboxMap extends React.Component<Props, State> {
	state: State = INITIAL_STATE
	mapContainer: HTMLElement
	geolocate = undefined
	mapZoommed = false // keep zoom when switch stops
	appData: AppMetaData = null
	renderePointMarkersOnce = false
	activeElevationMarker: mapboxgl.Marker

	private unListen: () => void

	constructor(props: Props) {
		super(props)

		const el = document.createElement('div')
		el.className = 'elevation-marker'
		this.activeElevationMarker = new mapboxgl.Marker(el)
	}

	componentDidMount(): void {
		// Use the next listener to resize the map
		// Ex: when a user was on the map, then opened another page and come back to the map. It will be resized using the next history listener
		this.unListen = ionHistory.listen(() => {
			// setTimeout can be 0, because we need it only for async resizing
			setTimeout(() => this.state.map.resize(), 0)
			this.props.onMapResize?.()
		})
	}

	componentDidUpdate = (prevProps: Props) => {
		if (this.state.map) {
			if (this.mapContainer && this.mapContainer.offsetWidth > 0 && this.mapContainer.offsetHeight > 0) {
				setTimeout(() => {
					this.state.map.resize()
					this.props.onMapResize?.()
				}, 0)
			}

			if (prevProps.currentItemIndex !== this.props.currentItemIndex && this.props.currentItemIndex != null) {
				this.setActiveMarkerIcon(this.props.items[this.props.currentItemIndex])

				const item = this.props.items[this.props.currentItemIndex]
				const { defaultZoom } = this.props

				if (item?.lng && item?.lat) {
					const ll = new mapboxgl.LngLat(Number(item.lng), Number(item.lat))
					this.state.map.flyTo({
						center: ll,
						zoom: defaultZoom || (this.mapZoommed ? this.state.map.getZoom() : 15),
					})
				}
			}

			if (this.props.routeProfile && prevProps.routeProfile != this.props.routeProfile) {
				this.getRoute(this.props.routeProfile, this.state.coordinates)
			}

			if (this.state.map.isStyleLoaded?.() && prevProps.mapRoutes !== this.props.mapRoutes && this.props.mapRoutes) {
				this.drawMapRoutes()
				this.fetchMediaItems()
			}

			// Always render even if they don't use media item
			if (
				this.state.mediaItemsLoaded &&
				(this.props.mediaItems !== prevProps.mediaItems ||
					(this.getMediaItemIds().length === 0 && !this.renderePointMarkersOnce))
			) {
				this.renderePointMarkersOnce = true
				this.createPointMarkers()
			}
		}
	}

	componentWillUnmount = () => {
		if (this.state.map) {
			this.state.map?.remove()
		}

		// Unbind all manually set up listeners
		this.state.markerListeners.forEach(listener => document.removeEventListener('click', listener))

		this.unListen()
	}

	setUpMap = async () => {
		const {
			items,
			currentItemIndex,
			mapStyle,
			extractMapboxMapStyle,
			defaultZoom,
			showDirections,
			routeProfile,
			onMapReady,
		} = this.props
		const hasCoordinates =
			items && items[currentItemIndex] && items[currentItemIndex].lat && items[currentItemIndex].lng

		const mapStyleId: string = extractMapboxMapStyle && extractMapboxMapStyle()
		const mapStyleUrl: string | undefined = mapStyleId && generateMapboxStyleUrl(mapStyleId)

		const mapOptions: mapboxgl.MapboxOptions = {
			container: this.mapContainer,
			style: mapStyle || mapStyleUrl,
			zoom: defaultZoom || 8,
		}

		let coordinates

		if (hasCoordinates) {
			mapOptions.center = [Number(items[currentItemIndex].lng), Number(items[currentItemIndex].lat)]
		} else if (showDirections) {
			coordinates = await getCurrentPosition()
			this.setState({ coordinates })
			mapOptions.center = [coordinates.lng, coordinates.lat]
		}

		this.geolocate = new mapboxgl.GeolocateControl({
			positionOptions: {
				enableHighAccuracy: true,
			},
			trackUserLocation: true,
		})

		const map = new mapboxgl.Map(mapOptions)
		map.addControl(new mapboxgl.NavigationControl(), isRTL() ? 'top-right' : 'top-left')
		map.addControl(this.geolocate)

		map.on('load', () => {
			if (showDirections) {
				this.getMyPosition()
				this.getRoute(routeProfile, coordinates)
			} else {
				this.createMarkers()
				this.drawMapRoutes()
				this.fetchMediaItems()
				this.initClickOnMap()
			}

			onMapReady?.()
		})

		this.setState({ map })
	}

	initClickOnMap = (): void => {
		this.state.map.on('click', e => {
			const features = this.state.map.queryRenderedFeatures(e.point, {
				layers: ['unclustered-point']
			})
			const feature = features?.[0]
			if (!feature) {
				this.setActiveMarkerIcon(null)
				this.props.onSetActiveItem?.(null, null)
			}
		})
	}

	getMediaItemIds = (): number[] => {
		const { mapRoutes } = this.props
		const ids: number[] = []
		const pointsRoute = mapRoutes.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		if (pointsRoute) {
			pointsRoute.features.forEach(f => {
				if (f.properties.media_item_id) {
					ids.push(f.properties.media_item_id)
				}
			})
		}

		return ids
	}

	fetchMediaItems = (): void => {
		const { mapRoutes, loadMediaItem } = this.props
		const { mediaItemsLoaded } = this.state
		const mediaItemIds = this.getMediaItemIds()
		const pointsRoute = mapRoutes.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		if (pointsRoute && !mediaItemsLoaded && loadMediaItem) {
			mediaItemIds.forEach(id => {
				loadMediaItem(id, 1, getCurrentLanguage())
			})
			this.setState({ mediaItemsLoaded: true })
		}
	}

	getPopupMaxWidth = (): string => {
		if (window.innerWidth >= 409 && window.innerWidth <= 540) {
			return '360px'
		} else if (window.innerWidth > 540) {
			return '460px'
		} else {
			return '300px'
		}
	}

	createPointMarker = async (feat: any): Promise<void> => {
		const {
			properties,
			geometry: { coordinates },
		} = feat
		const { icon, media_item_id } = properties

		const [iconStyle, iconColor, iconIcon]: string[] = icon?.split('_')

		const el = document.createElement('div')
		el.className = 'custom-marker'
		el.style.backgroundImage = this.generateMapPinIconUrl(iconStyle, iconColor.replace('#', ''), iconIcon)

		const mediaItem = this.props.mediaItems?.get(media_item_id)
		const popupHTML = buildPointMapPinPopup(feat, mediaItem?.item)

		const popup = new mapboxgl.Popup({
			offset: 50,
			closeButton: false,
			maxWidth: this.getPopupMaxWidth(),
		}).setHTML(popupHTML.innerHTML)

		const marker = new mapboxgl.Marker(el)
			.setLngLat([Number(coordinates[0]), Number(coordinates[1])])
			.setPopup(popup)
			.addTo(this.state.map)

		el.addEventListener('click', (e) => {
			// If point marker is on top of normal marker, prevent triggering the normal marker too
			e.stopPropagation()

			marker.togglePopup()
		})
	}

	createPointMarkers = (): void => {
		// It's probably a good idea to remove the old marker if this is called multiple times
		const pointsRoute = this.props.mapRoutes?.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		pointsRoute.features.map(f => this.createPointMarker(f))
	}

	drawMapRoutes = (): void => {
		const { mapRoutes, elevationEnabled } = this.props
		if (mapRoutes && mapRoutes.length > 0) {
			mapRoutes.forEach(route => {
				if (!this.state.map.getSource(`route${route?.attributes?.id}`)) {
					this.state.map.addSource(`route${route?.attributes?.id}`, {
						type: 'geojson',
						data: route,
					})

					if (route?.features?.[0]?.geometry?.type === 'Point') {
						this.state.map.addLayer({
							id: `routepoints${route?.attributes?.id}`,
							type: 'circle',
							source: `route${route?.attributes?.id}`,
							paint: {
								'circle-radius': 6,
								'circle-color': getStyleProperty('--nav-bg'),
							},
						})
					} else {
						this.state.map.addLayer({
							id: `routeline${route?.attributes?.id}`,
							type: 'line',
							source: `route${route?.attributes?.id}`,
							layout: {
								'line-join': 'round',
								'line-cap': 'round',
							},
							paint: {
								'line-color': ['coalesce', ['get', 'line-color'], getStyleProperty('--nav-bg')],
								'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 22, 12],
							},
						})

						// Make the clickable layer thicker to make it easier to click
						const clickableLineLayer = `routeline${route?.attributes?.id}-clickable`
						this.state.map.addLayer({
							id: clickableLineLayer,
							type: 'line',
							source: `route${route?.attributes?.id}`,
							layout: {
								'line-join': 'round',
								'line-cap': 'round',
							},
							paint: {
								'line-color': 'transparent',
								'line-width': 20,
							},
						})

						if (elevationEnabled) {
							const coordinates = route?.features?.[0]?.geometry?.coordinates
							this.state.map.on('click', clickableLineLayer, e => {
								this.handleLineClick(coordinates || [], [e.lngLat.lng, e.lngLat.lat])
							})
							this.state.map.on('mousemove', clickableLineLayer, e => {
								if (this.state.elevationOpened && this.state.currentRoute && this.state.currentRoute === coordinates) {
									this.handleLineClick(coordinates || [], [e.lngLat.lng, e.lngLat.lat])
								}
							})
						}
					}
				}
			})
		}
	}

	handleLineClick = (route: number[][], target: [number, number]) => {
		// Check for elevation
		if (route.length === 0 || typeof route[0][2] !== 'number') {
			return
		}

		const currentRoute: [number, number, number][] = route as [number, number, number][]

		const closestPointIndex = findClosestPointIndex(currentRoute, target)
		const closestPoint = currentRoute[closestPointIndex]

		this.activeElevationMarker.setLngLat([closestPoint[0], closestPoint[1]]).addTo(this.state.map)
		this.setState({ currentRoute, selectedRoutePointIndex: closestPointIndex, elevationOpened: true })
	}

	setActiveElevationMarkerCoords = (coords: [number, number]) => {
		this.activeElevationMarker.setLngLat(coords).addTo(this.state.map)
	}

	hideActiveElevationMarker = () => {
		this.activeElevationMarker.remove()
	}

	toggleElevationGraph = () => {
		this.setState({ selectedRoutePointIndex: undefined, elevationOpened: !this.state.elevationOpened })
	}

	createMarkers = async () => {
		const { items, boundAll } = this.props

		const features: GeoJSON.Feature[] = []
		const icons = new Set<string>()
		const collectionItemPins = []

		for await (const item of items) {
			const hasCoordinates = item.lat && item.lng

			if (hasCoordinates) {
				if (item.item_number && !this.appData) {
					this.appData = await loadAppMetaDataFromDisk(DEFAULT_APP_INFO.projectId)
				}

				const feature = this.convertItemToGeoJSON(item)

				icons.add(feature.properties.icon)
				icons.add(feature.properties.activeIcon)

				features.push(feature)
				collectionItemPins.push({ lat: Number(item.lat), lng: Number(item.lng) })
			}
		}

		const sourceId = 'CollectionItems'
		this.state.map.addSource(sourceId, {
			type: 'geojson',
			data: { type: 'FeatureCollection', features },
			cluster: features.length > 100,
			clusterMaxZoom: 8,
		})

		// Clusters layer
		this.state.map.addLayer({
			id: 'clusters',
			type: 'circle',
			source: sourceId,
			filter: ['has', 'point_count'],
			paint: {
				'circle-stroke-color': window.getComputedStyle(document.body).getPropertyValue('--button-secondary-bg'),
				'circle-stroke-width': 3,
				'circle-stroke-opacity': 1,
				'circle-color': window.getComputedStyle(document.body).getPropertyValue('--button-bg'),
				'circle-radius': 20,
			},
		})

		// Cluster count labels
		this.state.map.addLayer({
			id: 'cluster-count',
			type: 'symbol',
			source: sourceId,
			filter: ['has', 'point_count'],
			layout: {
				'text-field': ['get', 'point_count_abbreviated'],
				'text-font': ['Asap Regular'],
				'text-size': 16,
			},
			paint: {
				'text-color': 'white',
			},
		})

		// Unclustered symbol pins
		this.state.map.addLayer({
			id: 'unclustered-point',
			type: 'symbol',
			source: sourceId,
			filter: ['!', ['has', 'point_count']],
			layout: {
				'icon-image': ['get', 'icon'],
				'icon-size': 0.35,
				'icon-anchor': 'bottom',
				'icon-allow-overlap': true,
			},
		})

		this.state.map.on('click', 'unclustered-point', e => {
			const feature = e.features?.[0]
			if (!feature) {
				return
			}

			const { items, onSetActiveItem } = this.props
			const pageIndex = items?.findIndex(x => x.id === feature.id)
			const collectionItem = items?.[pageIndex]
			if (!collectionItem) {
				return
			}

			this.setActiveMarkerIcon(collectionItem)
			onSetActiveItem?.(collectionItem, pageIndex)
		})

		this.state.map.on('mouseenter', 'unclustered-point', () => {
			this.state.map.getCanvas().style.cursor = 'pointer'
		})
		this.state.map.on('mouseleave', 'unclustered-point', () => {
			this.state.map.getCanvas().style.cursor = ''
		})

		this.setState({ collectionItemPins })
		setTimeout(() => this.fitBounds(boundAll), 1000)

		await this.ensureItemPinImages(icons)
	}

	convertItemToGeoJSON = (item: CollectionItem): GeoJSON.Feature => {
		let { map_pin_colour: mapPinColour, map_pin_style: mapPinStyle, map_pin_icon: mapPinIcon } = item

		if ('stock_icon_pin_colour' in item) {
			const sectionLocationItem = item as unknown as MapItem
			mapPinColour = sectionLocationItem.stock_icon_pin_colour
			mapPinIcon = sectionLocationItem.stock_icon_pin_icon
			mapPinStyle = sectionLocationItem.stock_icon_pin_style as unknown as GetCollectionItemsIndexResponseData.MapPinStyleEnum
		}

		mapPinIcon = formatMapPinIcon({
			mapPinIcon,
			itemNumber: item.item_number,
			keypadEnabled: this.appData?.keypad_enabled,
			keypadDigits: this.appData?.keypad_digits,
		})

		const icon = `${mapPinStyle}_${mapPinColour}_${mapPinIcon}`
		const activeIcon = `${mapPinStyle}_${mapPinColour}_active`

		return {
			type: 'Feature',
			id: item.id,
			geometry: {
				type: 'Point',
				coordinates: [Number(item.lng), Number(item.lat)],
			},
			properties: {
				icon,
				activeIcon,
			},
		}
	}

	ensureItemPinImages = async (icons: Set<string>) => {
		const loadImage = (name: string, url: string) =>
			new Promise<void>((resolve) => {
			if (this.state.map.hasImage(name)) {
				return resolve()
			}

			this.state.map.loadImage(url, (error, image) => {
				if (error || !image) {
					console.log('ensureItemPinImages error', error)
					return resolve()
				}

				try {
					this.state.map.addImage(name, image)
				} catch (error) {
					console.log('ensureItemPinImages error', error)
				}
				resolve()
			})
		})

		const promises: Promise<void>[] = []
		icons.forEach((icon) => {
			const [mapPinStyle, mapPinColour, mapPinIcon] = icon.split('_')
			const url = generateMapPinIconUrl({ style: mapPinStyle, color: mapPinColour, icon: mapPinIcon })
			promises.push(loadImage(icon, url))
		})
		await Promise.all(promises)
	}

	setActiveMarkerIcon = (collectionItem: CollectionItem | ScreenSection) => {
		if (!collectionItem) {
			this.state.map.setLayoutProperty('unclustered-point', 'icon-image', ['get', 'icon'])
			return
		}

		this.state.map.setLayoutProperty('unclustered-point', 'icon-image', [
			'case',
			['==', ['id'], collectionItem.id],
			['get', 'activeIcon'],
			['get', 'icon']
		])
	}

	generateMapPinIconUrl = (style: string, color: string, icon: string): string =>
		`url(${generateMapPinIconUrl({ style, color, icon })})`

	isFeatureCollection = (geojson): boolean => geojson?.type === 'FeatureCollection'

	fitBounds = (showAllBounds?: boolean) => {
		const { collectionItemPins, map } = this.state
		const { currentItemIndex, items, defaultZoom, mapRoutes } = this.props
		const hasCoordinates =
			items && items[currentItemIndex] && items[currentItemIndex].lat && items[currentItemIndex].lng

		if (hasCoordinates && !showAllBounds) {
			const { lng, lat } = items[currentItemIndex]
			this.state.map.flyTo({
				center: [Number(lng || 0), Number(lat || 0)],
				zoom: defaultZoom || (this.mapZoommed ? this.state.map.getZoom() : 15),
				duration: 0,
			})
			this.mapZoommed = true
		}

		if ((!hasCoordinates || showAllBounds) && (collectionItemPins?.length || mapRoutes?.length)) {
			const bounds = new mapboxgl.LngLatBounds()

			collectionItemPins.map(pin => {
				bounds.extend(new mapboxgl.LngLat(pin.lng, pin.lat))
			})
			let filtered = mapRoutes?.map(route => {
				const isFeatureCollection = this.isFeatureCollection(route)
				if (isFeatureCollection) {
					route.features = route.features.filter(
						feat => 'coordinates' in feat.geometry && feat.geometry.coordinates.length > 0,
					)
				}
				return route
			})
			filtered = filtered.filter(route => {
				const isFeatureCollection = this.isFeatureCollection(route)
				if (
					isFeatureCollection ||
					(Array.isArray(route.features) && route.features[0]?.geometry && 'coordinates' in route.features[0]?.geometry)
				) {
					return route
				}
			})
			filtered.map(route => {
				route.features.map(feat => {
					const coords = feat.geometry.coordinates
					if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] == 'number' && typeof coords[1] == 'number') {
						bounds.extend(new mapboxgl.LngLat(coords[0], coords[1]))
					}
					else {
						feat.geometry.coordinates.map(coords => {
							bounds.extend(coords)
						})
					}
				})
			})
			if (!bounds.isEmpty()) {
				map.fitBounds(bounds, {
					padding: { top: 100, left: 60, right: 60, bottom: 150 },
					duration: 0,
				})
			}
		}
	}

	showAllBounds = (): void => this.fitBounds(true)

	getMyPosition = (): void => this.geolocate.trigger()

	onMountMapContainer = () => {
		this.setUpMap()
		this.setState({ mapContainerReady: true })
	}

	getRoute = async (routeProfile: RouteProfile, coordinates: SimpleCoordinates) => {
		setTimeout(() => this.state.map.resize())

		const { endCoordinates } = this.props

		if (!endCoordinates) {
			this.setState({ loading: false })
			return
		}

		const startCoords = `${coordinates.lng},${coordinates.lat}`
		const endCoords = `${endCoordinates.lng},${endCoordinates.lat}`
		try {
			const route = await getDirectionRoute(routeProfile, startCoords, endCoords)

			if (this.props.setLegsData && route.legs?.[0]) {
				this.props.setLegsData(route.legs[0])
			}

			const geojson: any = {
				type: 'Feature',
				properties: route.properties,
				geometry: route.geometry,
			}

			const layerOptions: any = {
				id: `directionRoute`,
				type: 'line',
				source: `directionRoute`,
				layout: {
					'line-join': 'round',
					'line-cap': 'round',
				},
				paint: {
					'line-color': ['coalesce', ['get', 'line-color'], '#3887be'],
					'line-width': 5,
					'line-opacity': 0.75,
				},
			}

			if (routeProfile === 'walking') {
				layerOptions.paint['line-dasharray'] = [1, 3]
			} else if (routeProfile === 'cycling') {
				layerOptions.paint['line-dasharray'] = [1, 1]
			}

			if (this.state.map.getLayer('directionRoute')) {
				this.state.map.removeLayer('directionRoute')
				this.state.map.removeSource('directionRoute')
			}

			this.state.map.addSource(`directionRoute`, {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [geojson],
				},
			})

			this.state.map.addLayer(layerOptions, 'waterway-label')

			const bounds = getBounds(
				route.geometry.coordinates.map((coords: number[]) => ({
					lng: coords[0],
					lat: coords[1],
				})),
			)

			this.setState({ loading: false })
			setTimeout(() => {
				this.state.map.fitBounds(bounds, {
					padding: { top: 100, left: 60, right: 60, bottom: 150 },
					duration: 0,
				})
			}, 1000)
		} catch (error) {
			console.error(error)
			this.setState({ error: JSON.stringify(error) })

			setTimeout(() => {
				this.setState({ loading: false })
				if (this.props.onCloseMap) {
					this.props.onCloseMap()
				}
			}, 3000)
		}
	}

	render() {
		const { mapControls, showDirections, items, currentItemIndex, isItemsLoaded } = this.props
		const { loading, error, mapContainerReady } = this.state

		return (
			<div
				style={{
					width: '100%',
					height: '100%',
				}}
				ref={(ref: HTMLDivElement) => {
					this.mapContainer = ref
					if (this.mapContainer && !this.state.mapContainerReady && (isItemsLoaded || showDirections)) {
						this.onMountMapContainer()
					}
				}}
			>
				{mapContainerReady &&
					mapControls &&
					mapControls({
						showAllBounds: this.showAllBounds,
						getMyPosition: this.getMyPosition,
						showInAppDirections: this.props.showInAppDirections,
					})}
				{this.state.currentRoute ? (
					<ElevationGraph
						route={this.state.currentRoute}
						defaultIndex={this.state.selectedRoutePointIndex}
						isOpen={this.state.elevationOpened}
						onMouseMove={this.setActiveElevationMarkerCoords}
						onMouseLeave={this.hideActiveElevationMarker}
						onToggle={this.toggleElevationGraph}
					/>
				) : null}
				{showDirections && <IonLoading isOpen={loading} message={strings.loading} />}
				<IonToast isOpen={!!error} message={error && error.substring(0, 160)} duration={3000} />
				<span className="hidden" aria-live="assertive">
					{items?.[currentItemIndex]?.title}
				</span>
			</div>
		)
	}
}
